import { useTu } from "@src/appV2/i18n/useTu";
import { formatLongDate, formatTime } from "@clipboard-health/date-time";
import { Box, Stack } from "@mui/material";
import { RateBoostLimitCard } from "@src/appV2/FacilityDashboard/VariableCharge/RateBoostLimitCard";
import { type Money } from "@src/appV2/lib/Money/types";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { type TimeSlot } from "@src/appV2/redesign/Shift/types";
import { BoostIncreaseRequestModal } from "@src/appV2/redesign/SpendingControls/components/SpendingLimitIncreaseRequestModal/BoostIncreaseRequestModal";
import type { UseModalState } from "@src/lib/ui-react";
import { useEffect, useRef, useState } from "react";
import { Avatar } from "../../../components/Avatar";
import { DialogFooter } from "../../../components/DialogFooter";
import { Divider } from "../../../components/Divider";
import { ResponsiveDialog } from "../../../components/ResponsiveDialog";
import { Text } from "../../../components/Text";
import { TimeSlotAvatar } from "../../../Shift/TimeSlotAvatar";
import { ChargeRateControls } from "../../ShiftDetails/components/ChargeRateControls";
import { minorToMajor } from "../../ShiftDetails/components/chargeRateUtils";
import { useInviteBoostSpendLimit } from "@src/appV2/redesign/SpendingControls/hooks/useInviteBoostSpendLimit";
import { type ChargeConstraints } from "./hooks/useInviteChargeRate";

interface EditInviteChargeRateDialogProps {
  modalState: UseModalState;
  workplaceId: string;
  qualification: string;
  baseChargeRate: Money;
  chargeConstraints: ChargeConstraints;
  currentBoost: number | undefined;
  onSave: (boost: number | undefined) => void;
  professionalName: string;
  professionalAvatarUrl?: string;
  qualificationDisplayName: string;
  startTime: Date;
  endTime: Date;
  timeSlot: TimeSlot;
  timezone: string;
}

const WORKER_BONUS_PERCENTAGE = 0.75;

export function EditInviteChargeRateDialog(props: EditInviteChargeRateDialogProps) {
  const { tu } = useTu("schedule");

  const {
    modalState,
    workplaceId,
    qualification,
    baseChargeRate,
    chargeConstraints,
    currentBoost,
    onSave,
    professionalName,
    professionalAvatarUrl,
    qualificationDisplayName,
    startTime,
    endTime,
    timeSlot,
    timezone,
  } = props;

  const baseRateInDollars = minorToMajor(baseChargeRate.amountInMinorUnits);

  const maxBoostInDollars =
    minorToMajor(chargeConstraints.max.amountInMinorUnits) - baseRateInDollars;

  const [boost, setBoost] = useState(currentBoost ?? 0);

  // Evaluate against the *live* boost so an over-limit rate is blocked at input, not after Save.
  const spendLimit = useInviteBoostSpendLimit({
    workplaceId,
    qualification,
    startTime,
    endTime,
    baseChargeRate,
    customChargeRateBoost: boost,
    enabled: modalState.modalIsOpen,
    editModalState: modalState,
  });
  const { increaseRequest } = spendLimit;

  // Seed the boost from `currentBoost` only on a fresh open — not when the editor reopens behind the
  // increase-request dialog (open() closes it, back() reopens it), which would wipe the in-progress
  // over-limit boost. Reset only on a full dismissal (both dialogs closed).
  const initializedRef = useRef(false);
  // `increaseRequest` is a fresh object each render (useModalState isn't memoized), so keep it out of
  // the deps via a ref and drive the effect off its stable `modalIsOpen` flag instead.
  const increaseRequestRef = useRef(increaseRequest);
  increaseRequestRef.current = increaseRequest;
  const isRequestModalOpen = increaseRequest.modalState.modalIsOpen;
  useEffect(() => {
    if (!modalState.modalIsOpen) {
      if (!isRequestModalOpen) {
        initializedRef.current = false;
        increaseRequestRef.current.reset();
      }
      return;
    }

    if (!initializedRef.current) {
      initializedRef.current = true;
      setBoost(currentBoost ?? 0);
    }
  }, [modalState.modalIsOpen, currentBoost, isRequestModalOpen]);

  const workerBonusInDollars =
    boost > 0 ? Math.floor(boost * WORKER_BONUS_PERCENTAGE * 100) / 100 : 0;

  const hasBoost = boost > 0;
  const isCheckingLimit = spendLimit.isActive && hasBoost && spendLimit.isLoading;
  const spendLimitBlocksSave = spendLimit.exceeded || isCheckingLimit;

  const handleSave = () => {
    onSave(boost > 0 ? boost : undefined);
    modalState.closeModal();
  };

  return (
    <>
      <ResponsiveDialog
        showCloseButton
        modalState={modalState}
        title={tu("Boost this shift")}
        width={450}
        footerContent={
          <DialogFooter
            primaryAction={{
              label: "Save",
              onClick: handleSave,
              isLoading: isCheckingLimit,
              disabled: spendLimitBlocksSave,
            }}
            secondaryAction={{ label: "Cancel", onClick: modalState.closeModal }}
          />
        }
      >
        <Stack spacing={5}>
          <Box
            sx={(theme) => ({
              border: `1px solid ${theme.palette.border?.base}`,
              borderRadius: theme.borderRadius?.xLarge,
              padding: 4,
            })}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={3}>
              <Stack direction="row" alignItems="center" spacing={3}>
                <Avatar alt={professionalName} size="medium" src={professionalAvatarUrl} />
                <Box>
                  <Text semibold variant="body2">
                    {professionalName}
                  </Text>
                  <Text variant="caption" sx={(theme) => ({ color: theme.palette.text.disabled })}>
                    {qualificationDisplayName}
                  </Text>
                </Box>
              </Stack>
              <Box sx={{ textAlign: "right" }}>
                <Text variant="caption" sx={(theme) => ({ color: theme.palette.text.disabled })}>
                  {tu("Hourly bonus")}
                </Text>
                <Text semibold variant="body2">
                  {formatDollarsAsUsd(workerBonusInDollars)}/hr
                </Text>
              </Box>
            </Stack>

            <Divider sx={{ marginY: 4, marginX: -4, width: "auto" }} />

            <Stack direction="row" alignItems="center" spacing={3}>
              <TimeSlotAvatar timeSlot={timeSlot} />
              <Box>
                <Text semibold variant="body2">
                  {`${formatTime(startTime, { timeZone: timezone })} - ${formatTime(endTime, { timeZone: timezone })}`}
                </Text>
                <Text variant="caption" sx={(theme) => ({ color: theme.palette.text.disabled })}>
                  {formatLongDate(startTime, { timeZone: timezone })}
                </Text>
              </Box>
            </Stack>
          </Box>

          <ChargeRateControls
            maxRate={maxBoostInDollars}
            maxRateDisplayValue={baseRateInDollars + boost}
            maxRateLabel="Boosted rate"
            minRate={0}
            minRateDisplayValue={baseRateInDollars}
            minRateLabel="Contract rate"
            rate={boost}
            title={tu("Extra hourly charge")}
            onRateChange={setBoost}
          />

          {spendLimit.exceeded && (
            <RateBoostLimitCard
              overageInMinorUnits={spendLimit.overageInMinorUnits}
              isRequested={increaseRequest.isRequested}
              onRequestIncrease={() => {
                increaseRequest.open({
                  overageInMinorUnits: spendLimit.overageInMinorUnits,
                  currentLimitInDollars:
                    spendLimit.limitInMinorUnits === undefined
                      ? undefined
                      : minorToMajor(spendLimit.limitInMinorUnits),
                  qualification,
                });
              }}
            />
          )}
        </Stack>
      </ResponsiveDialog>

      <BoostIncreaseRequestModal
        increaseRequest={increaseRequest}
        weekStartDate={spendLimit.weekStartDate}
        workplaceId={workplaceId}
      />
    </>
  );
}
